import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Spinner,
    Text,
    Alert,
    AlertIcon,
    Button,
    useToast,
    Center
} from '@chakra-ui/react';
import Header from '../../components/Header';
import CohortForm from './Forms/CohortForm';
import API from '../../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../../utils/DefaultFunction';

const EditCohorts = ({ header }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();

    const [cohortData, setCohortData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [permissions, setPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const permParams = "marketing/cohorts";

    const fetchPermissions = async () => {
        setPermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setPermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setPermissions([]);
        } finally {
            setPermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const xPermission = hasPermission("cohort-edit", permissions);

    // Fetch cohort data
    const fetchCohortData = async () => {
        if (!id) {
            setError('Cohort ID is required');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const url = `${process.env.REACT_APP_COHORT}/cohorts/${id}`;
     

            const response = await fetch(url, {
                method: 'GET',
                headers: getApiHeaders()
            });

            const data = await response.json();

            if (data.status) {
                // Map API response to expected format
                const mappedCohort = {
                    _id: data.data.cohort._id,
                    id: data.data.cohort._id,
                    name: data.data.cohort.name,
                    description: data.data.cohort.description || '',
                    refresh_frequency: data.data.cohort.refresh_frequency || 'daily',
                    refreshRate: data.data.cohort.refresh_frequency || 'daily',
                    match: data.data.cohort.match || 'all',
                    status: data.data.cohort.status,
                    user_count: data.data.cohort.user_count || 0,
                    conditions: data.data.cohort.conditions || [],
                    created_at: data.data.cohort.created_at,
                    updated_at: data.data.cohort.updated_at,
                    last_calculated_at: data.data.cohort.last_calculated_at,
                    next_calculation_at: data.data.cohort.next_calculation_at
                };

                setCohortData(mappedCohort);
             
            } else {
                setError(data.message || 'Failed to fetch cohort data');
                toast({
                    title: "Error",
                    description: data.message || 'Failed to fetch cohort data',
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching cohort:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch cohort data';
            setError(errorMessage);
            
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Load cohort data on component mount — wait for the permission check to
    // finish and pass before firing the real data fetch.
    useEffect(() => {
        if (permissionsLoading || !xPermission) return;
        fetchCohortData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, permissionsLoading, xPermission]);

    // Handle save after edit
    const handleSaveSuccess = (updatedCohortData) => {
        // toast({
        //     title: "Success",
        //     description: "Cohort updated successfully",
        //     status: "success",
        //     duration: 3000,
        //     isClosable: true,
        // });
        
        // Navigate back to cohorts list
      navigate(-1);
    };

    // Handle back navigation
    const handleBack = () => {
      navigate(-1);
    };

    // Handle retry
    const handleRetry = () => {
        fetchCohortData();
    };

    if (permissionsLoading) {
        return (
            <>
                <Header header={header} />
                <Center h="400px"><Spinner size="xl" color="blue.500" thickness="4px" /></Center>
            </>
        );
    }
    if (!xPermission) {
        return (
            <>
                <Header header={header} />
                <Center h="400px">
                    <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
                        <Text fontSize="sm" color="gray.500">You don't have permission to view this sections.</Text>
                    </Box>
                </Center>
            </>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <>
                <Header header={header} />
                <Box p={6} maxW="100%" mx="auto">
                    <Box textAlign="center" py={20}>
                        <Spinner size="xl" color="blue.500" />
                        <Text mt={4} fontSize="lg" color="gray.600">Loading cohort data...</Text>
                    </Box>
                </Box>
            </>
        );
    }

    // Error state
    if (error || !cohortData) {
        return (
            <>
                <Header header={header} />
                <Box p={6} maxW="100%" mx="auto">
                    <Alert status="error" mb={4}>
                        <AlertIcon />
                        <Box>
                            <Text fontWeight="bold">Error loading cohort</Text>
                            <Text fontSize="sm">{error || 'Cohort not found'}</Text>
                        </Box>
                    </Alert>
                    <Box textAlign="center" py={10}>
                        <Button
                            colorScheme="blue"
                            mr={4}
                            onClick={handleRetry}
                        >
                            Retry Loading
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleBack}
                        >
                            Back to Cohorts
                        </Button>
                    </Box>
                </Box>
            </>
        );
    }

    // Success state - render form with cohort data
    return (
        <>
            <Header header={header} />
            <CohortForm 
                editingCohort={cohortData}
                onSave={handleSaveSuccess}
                onBack={handleBack}
            />
        </>
    );
};

export default EditCohorts;